import { AgGridReact } from 'ag-grid-react'
import { agConfigSideBar, tableCellStyle } from '../../../../utils/AgGridConfig'
import { memo, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { CellClickedEvent, ColDef } from 'ag-grid-community'
import { Edit2, Check, X } from 'lucide-react'

import {
  AllTransactionsType,
  WatchlistEntityType,
  EditWatchlistNameMutation,
  useEditWatchlistNameMutation,
} from '../../../../generated/graphql'
import { KTSVG } from '../../../../_metronic/helpers'
import { GlobalContext } from '../../store/context/globalContext'
import { getCurrency, getSymbolOfCurrency } from '../../../../utils/CurrencyFormatter'
import {
  ActionRender,
  CompanyNameRender,
  CurrentValueRender,
  EsopHoldingRender,
  FdbPercentageRender,
  FounderHoldingRender,
  IndustryRender,
  LatestRaiseRender,
  MoicRender,
  TotalInvestmentRender,
  TotalRaisedRender,
  TotalRoundsRoundRender,
  ValuationRender,
  XirrRender,
} from './WatchlistAGComponent'
import { AddCompany } from './AddCompany'
import { useTableStateManagement } from '../../../../utils/TableHook'
import { TableId } from '../../../../utils/TableIdConstants'
import CloudSetting from '../../../../utils/tableStateIcon'
import { graphqlRequestClient, queryClient } from '../../../../queries/client'
import { useGetNewWatchlistsQuery, useGetWatchlistByIdQuery } from '../../../../generated/graphql'
import Toast, { ToastType } from '../../../../utils/toast'

type WatchlistTableProps = {
  watchlistCompanies: AllTransactionsType[]
  watchlistId: number
  watchlistName: string
  starredCompanies: string[]
  watchlistType?: WatchlistEntityType
}
export function WatchlistAGTable(props: WatchlistTableProps) {
  const gridApi = useRef<any>(null)
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const [showModal, setShowModal] = useState<boolean>(false)

  // States for inline editing watchlist name
  const [isEditingName, setIsEditingName] = useState<boolean>(false)
  const [editingWatchlistName, setEditingWatchlistName] = useState<string>('')
  const [editNameLoading, setEditNameLoading] = useState<boolean>(false)

  const userCurrencyFormat = getCurrency(userCurrency)
  // Edit watchlist name mutation
  const mutateEditWatchlistName = useEditWatchlistNameMutation<EditWatchlistNameMutation, Error>(
    graphqlRequestClient,
    {},
    {}
  )

  const existingCompanies: string[] = props.watchlistCompanies.map(
    (company) => company.companyData.hissaCompanyId || ''
  ) as string[]

  const [pageLoaded, setPageLoaded] = useState(false)
  const { filterCondition, setFilterCondition, isSaving, uploadFilter, uploadColumn, tableState } =
    useTableStateManagement(gridApi, TableId.watchlistTable + props.watchlistId + 'web', pageLoaded)

  const componentsRegistry = useMemo(
    () => ({
      companyName: memo(CompanyNameRender),
      valuation: memo(ValuationRender),
      noOfRound: memo(TotalRoundsRoundRender),
      latestRaise: memo(LatestRaiseRender),
      totalRaise: memo(TotalRaisedRender),
      esopHolding: memo(EsopHoldingRender),
      founderHolding: memo(FounderHoldingRender),
      totalInvestment: memo(TotalInvestmentRender),
      currentValue: memo(CurrentValueRender),
      moic: memo(MoicRender),
      xirr: memo(XirrRender),
      fdbPercentage: memo(FdbPercentageRender),
      industry: memo(IndustryRender),
    }),
    []
  )

  props.watchlistCompanies.map((company) => {})

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ['reset'],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  )

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState()
    return {}
  }

  function setColumnSetting(model: any) {
    if (gridApi.current) gridApi.current.columnApi.applyColumnState({ state: model })
    return {}
  }

  // Gets filter model via the grid API
  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel()
    return {}
  }

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model)
    return {}
  }

  const onClickTransactionName = (id: string) => null
  // ? cin
  //   ? navigate(`/inv/transaction/${transactionType}/${id}/${cin}`)
  //   : navigate(`/transactions/${transactionType}/${id}`)
  // : null;

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    if (cellParams.column.getColId() !== 'action-column') {
      const template = cellParams.data
      if (cellParams.column.getColId() === 'transactionName') {
        onClickTransactionName(template.id)
      }
    }
  }

  const [isColumnOpen, setIsColumnOpen] = useState(false)
  const [isFilterOpen, setIsFilterOpen] = useState(false)

  const [filteredRowData, setFilteredRowData] = useState<any>([])
  const [isFilterApplied, setIsFilterApplied] = useState(false)

  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent()
    setIsFilterApplied(filtersApplied)

    const filteredData = grid.api.getModel().rowsToDisplay.map((node: any) => node.data)
    setFilteredRowData(filteredData)

    uploadFilter()
  }

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel()

    const filteredData = gridApi.current?.api.getModel().rowsToDisplay.map((node: any) => node.data)

    setFilteredRowData(filteredData)
    gridApi.current?.api.setFilterModel(tableState?.filterState)
  }, [gridApi, tableState?.filterState, tableState?.columnState])

  const openToolPanel = (key: any) => {
    if (key === 'columns') {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key)
        else gridApi?.current?.api?.closeToolPanel()
        setIsColumnOpen((state) => !state)
        setIsFilterOpen(false)
      }
    } else if (key === 'filters') {
      if (gridApi) {
        if (!isFilterOpen) {
          gridApi?.current?.api?.openToolPanel(key)
        } else gridApi?.current?.api?.closeToolPanel()
        setIsFilterOpen((state) => !state)
        setIsColumnOpen(false)
      }
    }
  }

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting()
    const filterState = await getFilterSetting()
    // postOnFilter({
    //   userId: `${userId}`,
    //   companyId,
    //   filterData: {
    //     ...agTableStore.state,
    //     transactionColumnModel: columnState,
    //     transactionFilterModel: filterState,
    //   },
    // });
  }

  // Handle inline editing of watchlist name
  const handleEditNameClick = () => {
    if (props.watchlistName) {
      setEditingWatchlistName(props.watchlistName)
      setIsEditingName(true)
    }
  }

  const handleSaveWatchlistName = () => {
    if (!props.watchlistId || !editingWatchlistName.trim()) return

    setEditNameLoading(true)
    mutateEditWatchlistName.mutate(
      {
        input: {
          id: props.watchlistId,
          name: editingWatchlistName.trim(),
        },
      },
      {
        onSuccess: () => {
          // Invalidate both the watchlists list and the specific watchlist details
          queryClient.invalidateQueries(useGetNewWatchlistsQuery.getKey())
          queryClient.invalidateQueries(
            useGetWatchlistByIdQuery.getKey({ input: { id: props.watchlistId } })
          )
          setIsEditingName(false)
          setEditNameLoading(false)
          Toast('Watchlist name updated successfully!', ToastType.success)
        },
        onError: (error: any) => {
          setEditNameLoading(false)
          Toast(
            error.response?.errors?.[0]?.message || 'Error updating watchlist name',
            ToastType.error
          )
        },
      }
    )
  }

  const handleCancelEditName = () => {
    setIsEditingName(false)
    setEditingWatchlistName('')
  }

  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveWatchlistName()
    } else if (e.key === 'Escape') {
      handleCancelEditName()
    }
  }

  // Update local editing state when prop changes
  useEffect(() => {
    if (props.watchlistName && !isEditingName) {
      // Only update if we're not currently editing to avoid overriding user input
      setEditingWatchlistName(props.watchlistName)
    }
  }, [props.watchlistName, isEditingName])

  const columnDefs: ColDef[] = useMemo(() => {
    const baseColumns: ColDef[] = [
      {
        headerName: 'Company Name',
        field: 'companyName',
        cellRenderer: 'companyName',
        filter: 'agTextColumnFilter',
        filterValueGetter: (params) => {
          return [params.data?.dba, params.data?.companyName].filter(Boolean).join(' ')
        },
        getQuickFilterText: (params) => {
          return [params.data?.dba, params.data?.companyName].filter(Boolean).join(' ')
        },
        cellStyle: tableCellStyle,
        initialWidth: 200,
        minWidth: 350,
        sortable: true,
        suppressSizeToFit: true,
        menuTabs: ['filterMenuTab'],
        tooltipField: 'tooltipTransactionName',
      },
    ]

    const isInvestorWatchlist = props.watchlistType === WatchlistEntityType.Investor

    if (isInvestorWatchlist) {
      // Columns for investor watchlists
      return [
        ...baseColumns,
        {
          headerName: `Valuation (${getSymbolOfCurrency(userCurrency)})`,
          field: 'valuation',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'valuation',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipValuation',
        },
        {
          headerName: `Total Investment (${getSymbolOfCurrency(userCurrency)})`,
          field: 'totalInvestment',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'totalInvestment',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipTotalInvestment',
        },
        {
          headerName: `Current Value (${getSymbolOfCurrency(userCurrency)})`,
          field: 'currentValue',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'currentValue',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipCurrentValue',
        },
        {
          headerName: 'MOIC',
          field: 'moic',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'moic',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
        },
        {
          headerName: 'XIRR',
          field: 'xirr',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'xirr',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
        },
        {
          headerName: 'FDB Percentage',
          field: 'fdbPercentage',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'fdbPercentage',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
        },
        {
          headerName: 'Industry',
          field: 'industry',
          filter: 'agTextColumnFilter',
          cellStyle: tableCellStyle,
          sortable: true,
          cellRenderer: 'industry',
          menuTabs: ['filterMenuTab'],
        },
        // {
        //   headerName: '',
        //   field: 'actions',
        //   hide: false,
        //   width: 220,
        //   pinned: 'right',
        //   maxWidth: 220,
        //   filter: false,
        //   menuTabs: [],
        //   colId: 'action-column',
        //   suppressNavigable: true,
        //   suppressColumnsToolPanel: true,
        //   resizable: true,
        //   sortable: false,
        //   cellRenderer: ActionRender,
        // },
        {
          headerName: '',
          field: 'actions',
          pinned: 'right',
          hide: false,
          width: 60,
          maxWidth: 60,
          filter: false,
          menuTabs: [],
          colId: 'action-column',
          suppressNavigable: true,
          suppressColumnsToolPanel: true,
          cellStyle: {
            'padding-left': '9px',
            'padding-right': '4px',
            'padding-top': '5px',
            'line-height': '20px',
          },
          resizable: false,
          sortable: false,
          cellRenderer: ActionRender,
        },
      ]
    } else {
      // Columns for regular watchlists
      return [
        ...baseColumns,
        {
          headerName: `Valuation (${getSymbolOfCurrency(userCurrency)})`,
          field: 'valuation',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'valuation',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipValuation',
        },
        {
          headerName: 'No. of Rounds',
          field: 'noOfRound',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'noOfRound',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
        },
        {
          headerName: `Latest Raise (${getSymbolOfCurrency(userCurrency)})`,
          field: 'latestRaise',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'latestRaise',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipLatestRaise',
        },
        {
          headerName: `Total Raised (${getSymbolOfCurrency(userCurrency)})`,
          field: 'totalRaise',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'totalRaise',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipTotalRaised',
        },
        {
          headerName: `ESOP Holding (%)`,
          field: 'esopHolding',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'esopHolding',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipEsopHolding',
        },
        {
          headerName: `Founder Holding (%)`,
          field: 'founderHolding',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'founderHolding',
          cellStyle: tableCellStyle,
          sortable: true,
          menuTabs: ['filterMenuTab'],
          tooltipField: 'tooltipFounderHolding',
        },
        {
          headerName: 'Industry',
          field: 'industry',
          filter: 'agTextColumnFilter',
          cellStyle: tableCellStyle,
          sortable: true,
          cellRenderer: 'industry',
          menuTabs: ['filterMenuTab'],
        },
        // {
        //   headerName: '',
        //   field: 'actions',
        //   hide: false,
        //   width: 120,
        //   pinned: 'right',
        //   maxWidth: 120,
        //   filter: false,
        //   menuTabs: [],
        //   colId: 'action-column',
        //   suppressNavigable: true,
        //   suppressColumnsToolPanel: true,
        //   resizable: true,
        //   sortable: false,
        //   cellRenderer: ActionRender,
        // },
        {
          headerName: '',
          field: 'actions',
          pinned: 'right',
          hide: false,
          width: 60,
          maxWidth: 60,
          filter: false,
          menuTabs: [],
          colId: 'action-column',
          suppressNavigable: true,
          suppressColumnsToolPanel: true,
          cellStyle: {
            'padding-left': '9px',
            'padding-right': '4px',
            'padding-top': '5px',
            'line-height': '20px',
          },
          resizable: false,
          sortable: false,
          cellRenderer: ActionRender,
        },
      ]
    }
  }, [userCurrencyFormat, props.watchlistType, userCurrency])

  const rowData = useMemo(() => {
    setPageLoaded(true)
    return props.watchlistCompanies?.map((company) => {
      const isInvestorWatchlist = props.watchlistType === WatchlistEntityType.Investor

      const baseData = {
        companyId: company.companyData.hissaCompanyId,
        companyName: company.companyData.name,
        industry: company.companyData.industry || '-',
        dba: company.companyData.dba,
        logo: company.companyData.logo,
        companyCurrency: company.companyData.companyCurrency,
        userCurrencyFormat: userCurrencyFormat,
        userCurrency: userCurrency,
        watchlistId: props.watchlistId,
        watchlistName: props.watchlistName,
        starredCompanies: props.starredCompanies,
        existingCompanies: existingCompanies,
        watchlistType: props.watchlistType,
        companyData: company.companyData,
      }

      if (isInvestorWatchlist && company.aggregateData) {
        // For investor watchlists, use aggregateData + valuation from companyData
        return {
          ...baseData,
          valuation: company.companyData.valuation,
          tooltipValuation: company.companyData.valuation?.toLocaleString(userCurrencyFormat),
          totalInvestment: company.aggregateData.totalInvestment || 0,
          tooltipTotalInvestment: (company.aggregateData.totalInvestment || 0)?.toLocaleString(
            userCurrencyFormat
          ),
          currentValue: company.aggregateData.currentValue || 0,
          tooltipCurrentValue: (company.aggregateData.currentValue || 0)?.toLocaleString(
            userCurrencyFormat
          ),
          moic: company.aggregateData.moic || 0,
          xirr: company.aggregateData.irr || 0, // IRR from GraphQL is actually XIRR
          fdbPercentage: company.aggregateData.currentHoldingPercentage
            ? company.aggregateData.currentHoldingPercentage * 100
            : 0,
        }
      } else {
        // For regular watchlists, use existing company data
        return {
          ...baseData,
          valuation: company.companyData.valuation,
          tooltipValuation: company.companyData.valuation?.toLocaleString(userCurrencyFormat),
          esopHolding: company.companyData.esopHolding ? company.companyData.esopHolding * 100 : 0,
          tooltipEsopHolding: (company.companyData.esopHolding
            ? company.companyData.esopHolding * 100
            : 0
          )?.toLocaleString(userCurrencyFormat),
          founderHolding: company.companyData.founderHoldingPercentage
            ? company.companyData.founderHoldingPercentage * 100
            : 0,
          tooltipFounderHolding: (company.companyData.founderHoldingPercentage
            ? company.companyData.founderHoldingPercentage * 100
            : 0
          )?.toLocaleString(userCurrencyFormat),
          noOfRound: company.companyData.noOfRounds,
          latestRaise: company.companyData.latestRaise,
          tooltipLatestRaise: company.companyData.latestRaise?.toLocaleString(userCurrencyFormat),
          totalRaise: company.companyData.totalFunding,
          tooltipTotalRaised: company.companyData.totalFunding?.toLocaleString(userCurrencyFormat),
        }
      }
    })
  }, [props.watchlistCompanies, userCurrencyFormat, props.starredCompanies, props.watchlistType])

  return (
    <div
      className='card p-0 h-575px'
      style={{
        boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)',
        border: '1',
        borderColor: '#F1F1F2',
      }}
    >
      <div className='d-flex flex-row border-0 p-9'>
        <div className='card-title align-items-end flex-column'>
          {!isEditingName ? (
            <div className='d-flex align-items-center'>
              <h1 className='mb-0'>{props.watchlistName || 'Watchlist'}</h1>
              {props.watchlistType === WatchlistEntityType.Company && props.watchlistName && (
                <button
                  className='btn btn-sm btn-icon btn-light p-0 text-hover-primary'
                  onClick={handleEditNameClick}
                  title='Edit watchlist name'
                  aria-label='Edit watchlist name'
                  style={{ background: 'transparent', border: 'none' }}
                >
                  <Edit2 size={16} className='text-gray-600' />
                </button>
              )}
            </div>
          ) : (
            <div className='d-flex align-items-center'>
              <input
                type='text'
                className='form-control me-2'
                value={editingWatchlistName}
                onChange={(e) => setEditingWatchlistName(e.target.value)}
                onKeyDown={handleNameKeyPress}
                autoFocus
                style={{
                  minWidth: '150px',
                  maxWidth: '175px',
                  border: '1px solid #e9ecef',
                }}
              />
              <button
                className='btn btn-sm btn-icon btn-light-success me-2'
                onClick={handleSaveWatchlistName}
                disabled={editNameLoading || !editingWatchlistName.trim()}
                title='Save changes'
              >
                {editNameLoading ? (
                  <span className='spinner-border spinner-border-sm'></span>
                ) : (
                  <Check size={16} />
                )}
              </button>
              <button
                className='btn btn-sm btn-icon btn-light-danger'
                onClick={handleCancelEditName}
                disabled={editNameLoading}
                title='Cancel editing'
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
        <div className='col d-flex flex-row justify-content-end'>
          <CloudSetting
            tableId={TableId.watchlistTable + props.watchlistId + 'web'}
            isLoading={isSaving}
            filterCondition={filterCondition}
            setFilterCondition={setFilterCondition}
          />
          {props.watchlistType !== WatchlistEntityType.Investor && (
            <>
              <button
                type='submit'
                onClick={() => setShowModal(!showModal)}
                className='px-4 rounded border border-0 border-primary bg-primary h-38px fw-normal ms-4 text-white'
              >
                <KTSVG
                  path='media/icons/duotune/general/gen041.svg'
                  className='svg-icon-3 text-white'
                />{' '}
                Add Company
              </button>
              {showModal && (
                <AddCompany
                  showModal={showModal}
                  handleClose={() => setShowModal(false)}
                  watchlistId={props.watchlistId}
                  watchlistName={props.watchlistName}
                  existingCompanies={existingCompanies}
                />
              )}
            </>
          )}
          <button
            type='button'
            value={'Columns'}
            onClick={() => openToolPanel('columns')}
            className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal ms-4 text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/layouts/lay004.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Columns
          </button>
          <button
            value={'Filters'}
            onClick={() => openToolPanel('filters')}
            className='px-4 rounded border border-1 border-secondary-txt bg-transparent h-38px fw-normal ms-4 text-hover-primary'
          >
            <KTSVG
              path='/media/icons/duotune/general/gen031.svg'
              className='svg-icon-3 text-secondary-txt'
            />{' '}
            Filter
          </button>
        </div>
      </div>
      <div className='card-body w-100 p-9 pt-0 overflow-auto ag-theme-material h-100'>
        <AgGridReact
          sideBar={agConfigSideBar}
          onGridReady={(params) => {
            gridApi.current = params
          }}
          getRowId={(params) => params.data.companyId}
          components={componentsRegistry}
          alwaysShowHorizontalScroll
          alwaysMultiSort
          animateRows={true}
          defaultColDef={defaultColDef}
          onFilterChanged={onAgGridFilterChanged}
          onColumnMoved={uploadColumn}
          onColumnEverythingChanged={uploadColumn}
          onColumnResized={uploadColumn}
          rowData={rowData}
          columnDefs={columnDefs}
          pagination={false}
          enableCharts={true}
          enableRangeSelection={true}
          suppressRowTransform={true}
          suppressCopyRowsToClipboard={true}
          suppressCopySingleCellRanges={true}
          suppressCellFocus={true}
          suppressMenuHide={false}
          tooltipShowDelay={50}
          tooltipInteraction={true}
          rowClass={'border-right-0 border-left-0 border-dashed'}
          overlayNoRowsTemplate={
            '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
          }
          getRowStyle={(params) => {
            if (params.rowIndex % 2 === 0) {
              return { background: '#f8f8f8' }
            } else {
              return { background: '#ffffff' }
            }
          }}
        ></AgGridReact>
      </div>
      <div className='position-absolute bottom-0 end-0 p-3'>
        <small className='text-muted fst-italic' style={{ fontSize: '0.75rem' }}>
          * Data might not be updated and can be incorrect.
        </small>
      </div>
    </div>
  )
}
